import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import useStore from "../../../store/useStore";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { FaSpinner } from "react-icons/fa";
import { openCashfreeSubscription } from "../../../utils/cashfree";

import { STATUS_TABS } from "./subscriptions/subscriptionUtils";
import ResumeModal from "./subscriptions/ResumeModal";
import SubscriptionCard from "./subscriptions/SubscriptionCard";
import ProductShowcaseCard from "./subscriptions/ProductShowcaseCard";

const SubscriptionsSection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("");
  const [resumeTarget, setResumeTarget] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const {
    subscription_config_loading,
    subscription_products,
    subscriptions,
    subscriptions_loading,
    subscriptions_error,
    subscription_action_loading,
    fetchSubscriptionConfig,
    fetchSubscriptions,
    getSubscriptionMandateCheckout,
    resumeSubscription,
    cancelSubscription,
  } = useStore();

  useEffect(() => {
    fetchSubscriptionConfig();
  }, [fetchSubscriptionConfig]);

  useEffect(() => {
    fetchSubscriptions(status);
  }, [fetchSubscriptions, status]);

  useEffect(() => {
    const productId = searchParams.get("productId");
    const variantId = searchParams.get("variantId");
    if (productId) {
      navigate(`/subscription/start?productId=${encodeURIComponent(productId)}${variantId ? `&variantId=${encodeURIComponent(variantId)}` : ""}`, { replace: true });
    }
  }, [navigate, searchParams]);

  const refreshCurrentTab = () => fetchSubscriptions(status);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refreshCurrentTab(),
        new Promise((resolve) => setTimeout(resolve, 1200)),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSubscribeFromShowcase = (productId, variantId) => {
    const query = new URLSearchParams();
    if (productId) query.set("productId", productId);
    if (variantId) query.set("variantId", variantId);
    navigate(`/subscription/start${query.size ? `?${query.toString()}` : ""}`);
  };

  const handleResume = async (resumeDate) => {
    try {
      await resumeSubscription(resumeTarget.subscription_id, resumeDate);
      toast.success("Subscription resumed successfully");
      setResumeTarget(null);
      await refreshCurrentTab();
    } catch (error) {
      toast.error(error.message || "Failed to resume subscription");
    }
  };

  const handleSetupAutopay = async (subscription) => {
    try {
      const mandateCheckout = getSubscriptionMandateCheckout(subscription);
      await openCashfreeSubscription(mandateCheckout);
    } catch (error) {
      toast.error(error.message || "Failed to start UPI AutoPay setup");
    }
  };

  const handleCancel = async (subscription) => {
    const confirmed = window.confirm(
      "Cancel this subscription permanently? This cannot be undone.",
    );
    if (!confirmed) return;

    try {
      await cancelSubscription(subscription.subscription_id);
      toast.success("Subscription cancelled");
      await refreshCurrentTab();
    } catch (error) {
      toast.error(error.message || "Failed to cancel subscription");
    }
  };

  return (
    <div className="pt-1 sm:pt-2">
      <div className="mb-5 rounded-3xl border border-primary-100 bg-gradient-to-br from-primary-50 via-white to-emerald-50 p-4 shadow-sm sm:mb-6 sm:flex sm:flex-row sm:items-center sm:justify-between sm:rounded-none sm:border-0 sm:bg-none sm:p-0 sm:shadow-none">
        <div>
          <span className="mb-2 inline-flex rounded-full bg-primary-100 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-[0.16em] text-primary-700 sm:hidden">
            Scheduled goodness
          </span>
          <h1 className="text-xl font-extrabold tracking-tight text-gray-900">
            My Subscriptions
          </h1>
          <p className="mt-1 text-xs leading-relaxed text-gray-500 sm:mt-0.5 sm:text-sm sm:text-gray-400">
            Manage daily milk plans, pauses, and delivery history
          </p>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2 sm:mt-0 sm:flex">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={subscriptions_loading || isRefreshing}
            className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-xs font-bold text-gray-600 shadow-sm transition hover:bg-gray-50 disabled:opacity-50 sm:gap-2 sm:px-4 sm:text-sm sm:shadow-none"
          >
            {subscriptions_loading || isRefreshing ? (
              <FaSpinner className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshOutlinedIcon style={{ fontSize: 17 }} />
            )}
            {subscriptions_loading || isRefreshing ? "Refreshing..." : "Refresh"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/subscription/start")}
            className="flex items-center justify-center gap-1.5 rounded-xl bg-primary-600 px-3 py-2.5 text-xs font-extrabold text-white shadow-md shadow-primary-600/20 transition hover:bg-primary-700 sm:gap-2 sm:px-4 sm:text-sm"
          >
            <AddOutlinedIcon style={{ fontSize: 17 }} />
            Subscribe
          </button>
        </div>
      </div>

      {/* Mobile: filter trigger button */}
      <div className="mb-5 sm:hidden">
        <button
          type="button"
          onClick={() => setFilterSheetOpen(true)}
          className="flex w-full items-center justify-between rounded-2xl border border-gray-200 bg-white px-4 py-3.5 shadow-sm active:bg-gray-50"
        >
          <div className="flex items-center gap-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-50">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-primary-600" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM5 9a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm2 4a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </span>
            <div className="text-left">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Filter by status</p>
              <p className="text-sm font-extrabold text-gray-800">
                {STATUS_TABS.find((t) => t.value === status)?.label || "All Subscriptions"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {status !== "" && (
              <span className="rounded-full bg-primary-600 px-2 py-0.5 text-[10px] font-extrabold text-white">Active</span>
            )}
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.085l3.71-3.855a.75.75 0 111.08 1.04l-4.25 4.415a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>
          </div>
        </button>
      </div>

      {/* Mobile: bottom-sheet filter panel */}
      {filterSheetOpen && (
        <div className="fixed inset-0 z-[80] sm:hidden" style={{ isolation: "isolate" }}>
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setFilterSheetOpen(false)}
          />
          {/* Sheet */}
          <div
            className="absolute bottom-0 left-0 right-0 rounded-t-3xl bg-white shadow-2xl"
            style={{ animation: "slideUp 0.25s ease-out" }}
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="h-1 w-10 rounded-full bg-gray-200" />
            </div>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3">
              <h3 className="text-base font-extrabold text-gray-900">Filter Subscriptions</h3>
              <button
                type="button"
                onClick={() => setFilterSheetOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            {/* Options */}
            <div className="px-4 pb-8 pt-1 space-y-2">
              {STATUS_TABS.map((tab) => {
                const isActive = status === tab.value;
                return (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => { setStatus(tab.value); setFilterSheetOpen(false); }}
                    className={`flex w-full items-center justify-between rounded-2xl px-4 py-3.5 text-sm font-bold transition ${
                      isActive
                        ? "bg-primary-600 text-white shadow-md shadow-primary-600/20"
                        : "bg-gray-50 text-gray-700 active:bg-gray-100"
                    }`}
                  >
                    <span>{tab.label}</span>
                    {isActive && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Desktop: tab pills */}
      <div className="mb-5 hidden gap-2 sm:flex">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.label}
            type="button"
            onClick={() => setStatus(tab.value)}
            className={`shrink-0 rounded-xl px-4 py-2 text-sm font-bold transition ${
              status === tab.value
                ? "bg-primary-600 text-white shadow-md shadow-primary-600/15"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {subscriptions_error && (
        <div className="mb-5 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-red-600">
          {subscriptions_error}
        </div>
      )}

      {subscriptions_loading && subscriptions.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-3 py-20">
          <FaSpinner className="h-8 w-8 animate-spin text-primary-600" />
          <p className="text-sm font-semibold text-gray-400">
            Loading subscriptions
          </p>
        </div>
      )}

      {!subscriptions_loading && subscriptions.length === 0 && (
        <div className="rounded-3xl border-2 border-dashed border-gray-200 bg-gray-50/60 px-5 py-10 text-center sm:py-14">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-50 text-primary-600">
            <AutorenewOutlinedIcon style={{ fontSize: 34 }} />
          </div>
          <h2 className="text-lg font-extrabold text-gray-900">
            No subscriptions found
          </h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
            Start a weekly or monthly milk subscription and manage delivery
            changes from here.
          </p>
          <button
            type="button"
            onClick={() => navigate("/subscription/start")}
            className="mt-5 rounded-xl bg-primary-600 px-5 py-3 text-sm font-extrabold text-white hover:bg-primary-700"
          >
            Start Subscription
          </button>
        </div>
      )}

      {subscriptions.length > 0 && (
        <div className="space-y-4">
          {subscriptions.map((subscription) => (
            <SubscriptionCard
              key={subscription.subscription_id}
              subscription={subscription}
              onDetail={(item) => navigate(`/account/subscriptions/${item.subscription_id}/view`)}
              onPause={(item) => navigate(`/account/subscriptions/${item.subscription_id}/pause`)}
              onResume={setResumeTarget}
              onEdit={(item) => navigate(`/account/subscriptions/${item.subscription_id}/edit`)}
              onCancel={handleCancel}
              onSetupAutopay={handleSetupAutopay}
              actionLoading={subscription_action_loading}
            />
          ))}
        </div>
      )}

      {/* Available Milk Products */}
      <div className="mt-7 border-t border-gray-100 pt-6 sm:mt-8 sm:border-0 sm:pt-0">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-base font-extrabold text-gray-900">
              🥛 Available Milk Products
            </h2>
            <p className="mt-0.5 text-xs font-semibold text-gray-400">
              Pick a product below to start a new subscription
            </p>
          </div>
        </div>

        {subscription_config_loading && subscription_products.length === 0 && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
            {[1, 2].map((n) => (
              <div
                key={n}
                className="h-72 animate-pulse rounded-2xl bg-gray-100"
              />
            ))}
          </div>
        )}

        {!subscription_config_loading && subscription_products.length === 0 && (
          <div className="rounded-2xl border border-dashed border-gray-200 py-10 text-center">
            <Inventory2OutlinedIcon
              className="text-gray-300"
              style={{ fontSize: 36 }}
            />
            <p className="mt-2 text-sm font-bold text-gray-400">
              No products available right now
            </p>
          </div>
        )}

        {subscription_products.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {subscription_products.map((product) => (
              <ProductShowcaseCard
                key={product.product_id}
                product={product}
                onSubscribe={handleSubscribeFromShowcase}
              />
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-2 text-sm sm:grid-cols-3 sm:gap-3">
        <div className="flex items-center gap-3 rounded-2xl bg-primary-50 px-4 py-3 text-primary-800 sm:block">
          <CalendarMonthOutlinedIcon style={{ fontSize: 18 }} />
          <p className="font-bold sm:mt-1">Weekly and monthly plans</p>
        </div>
        <div className="flex items-center gap-3 rounded-2xl bg-blue-50 px-4 py-3 text-blue-800 sm:block">
          <LocalShippingOutlinedIcon style={{ fontSize: 18 }} />
          <p className="font-bold sm:mt-1">Delivery log from server schedule</p>
        </div>
        <div className="flex items-center gap-3 rounded-2xl bg-amber-50 px-4 py-3 text-amber-800 sm:block">
          <PaymentsOutlinedIcon style={{ fontSize: 18 }} />
          <p className="font-bold sm:mt-1">Wallet, UPI, or monthly billing</p>
        </div>
      </div>

      {resumeTarget && (
        <ResumeModal
          subscription={resumeTarget}
          loading={subscription_action_loading}
          onSubmit={handleResume}
          onClose={() => setResumeTarget(null)}
        />
      )}

    </div>
  );
};

export default SubscriptionsSection;
